// Seller shell warning strip for the monthly subscription (Volkan 2026-11-01,
// plan part 3). Shown above every seller page when:
//   · a subscription payment failed — until it is paid (danger), or
//   · the free trial ends within a week and the seller has no saved card
//     (warning; Stripe would pause the subscription).
// Reads the database-only /subscription/status, never Stripe. Owners only:
// the shell renders it for the members who can open the wallet.

import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { formatPlainDate, plainDateForInstant } from '../../lib/datetime'
import { useT } from '../../i18n'
import { sellerSubscriptionI18n } from '../../i18n/wallet/subscription'
import {
  fetchSubscriptionStatus,
  SUBSCRIPTION_STATUS_QUERY,
  type SellerSubscriptionStatus,
} from './subscription-api'

const WEEK_MS = 7 * 24 * 60 * 60 * 1000

function day(value: string | null): string {
  if (!value) return ''
  const plain = plainDateForInstant(value)
  return plain ? formatPlainDate(plain) : ''
}

function subscriptionStripKind(
  s: SellerSubscriptionStatus | undefined,
  now: number,
): 'unpaid' | 'trialNoCard' | null {
  if (!s) return null
  if (s.payment_failed_at) return 'unpaid'
  if (
    s.subscription_status === 'trialing' &&
    s.trial_end &&
    !s.has_saved_card &&
    new Date(s.trial_end).getTime() - now <= WEEK_MS
  ) {
    return 'trialNoCard'
  }
  return null
}

export function SubscriptionStrip() {
  const t = useT(sellerSubscriptionI18n)
  const query = useQuery({
    queryKey: SUBSCRIPTION_STATUS_QUERY,
    queryFn: fetchSubscriptionStatus,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  const kind = subscriptionStripKind(query.data, query.dataUpdatedAt)
  if (!kind || !query.data) return null

  const danger = kind === 'unpaid'
  const text =
    kind === 'unpaid'
      ? t.strip.unpaid(day(query.data.restricted_after))
      : t.strip.trialNoCard(day(query.data.trial_end))

  return (
    <div
      role={danger ? 'alert' : 'status'}
      data-testid="subscription-strip"
      className={`flex flex-wrap items-center justify-between gap-2 border-b px-[var(--page-padding-x)] py-2 text-sm ${
        danger
          ? 'border-[color-mix(in_srgb,var(--color-danger)_24%,transparent)] bg-[color-mix(in_srgb,var(--color-danger)_8%,var(--color-surface))] text-[var(--color-danger)]'
          : 'border-[color-mix(in_srgb,var(--color-warning)_24%,transparent)] bg-[color-mix(in_srgb,var(--color-warning)_8%,var(--color-surface))] text-[var(--color-text)]'
      }`}
    >
      <span>{text}</span>
      <Link to="/seller/wallet/subscription" className="font-medium underline underline-offset-4">
        {t.strip.action}
      </Link>
    </div>
  )
}
